import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, CheckSquare, Droplets, Activity, Calendar, Smile, Zap } from 'lucide-react';
import { toast } from 'sonner';

const MOOD_OPTIONS = [
  { value: 'great', emoji: '😊', label: 'Great' },
  { value: 'good', emoji: '🙂', label: 'Good' },
  { value: 'okay', emoji: '😐', label: 'Okay' },
  { value: 'sad', emoji: '😢', label: 'Sad' },
  { value: 'anxious', emoji: '😰', label: 'Anxious' },
  { value: 'stressed', emoji: '😤', label: 'Stressed' },
  { value: 'tired', emoji: '😴', label: 'Tired' },
  { value: 'motivated', emoji: '💪', label: 'Motivated' },
];

const QuickActionButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [taskTitle, setTaskTitle] = useState('');
  const [waterGlasses, setWaterGlasses] = useState(1);
  const [selectedMood, setSelectedMood] = useState(null);
  const [energyLevel, setEnergyLevel] = useState(5);
  const [loading, setLoading] = useState(false);

  const handleQuickAction = async (type, data) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assistant/quick-action`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ action_type: type, data })
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success('✅ Success!', {
          description: result.message
        });
        resetAndClose();
      }
    } catch (error) {
      console.error('Quick action error:', error);
      toast.error('Action Failed', {
        description: 'Unable to complete action'
      });
    }
  };

  const handleMoodLog = async () => {
    if (!selectedMood) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/mood/log`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            mood: selectedMood,
            energy_level: energyLevel
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success('✨ Mood Logged!', {
          description: result.message
        });
        resetAndClose();
      } else {
        throw new Error('Failed to log mood');
      }
    } catch (error) {
      console.error('Mood log error:', error);
      toast.error('Failed to log mood', {
        description: 'Please try again'
      });
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setIsOpen(false);
    setActionType(null);
    setTaskTitle('');
    setWaterGlasses(1);
    setSelectedMood(null);
    setEnergyLevel(5);
  };

  const actions = [
    {
      type: 'mood',
      icon: Smile,
      label: 'Log Mood',
      color: 'text-pink-500',
      bgColor: 'bg-pink-50 hover:bg-pink-100'
    },
    {
      type: 'task',
      icon: CheckSquare,
      label: 'Quick Task',
      color: 'text-blue-500',
      bgColor: 'bg-blue-50 hover:bg-blue-100'
    },
    {
      type: 'water',
      icon: Droplets,
      label: 'Log Water',
      color: 'text-cyan-500',
      bgColor: 'bg-cyan-50 hover:bg-cyan-100'
    },
    {
      type: 'workout',
      icon: Activity,
      label: 'Log Workout',
      color: 'text-orange-500',
      bgColor: 'bg-orange-50 hover:bg-orange-100'
    },
    {
      type: 'event',
      icon: Calendar,
      label: 'Add Event',
      color: 'text-purple-500',
      bgColor: 'bg-purple-50 hover:bg-purple-100'
    }
  ];

  return (
    <>
      {/* Floating Action Button */}
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 shadow-lg z-50"
        size="icon"
      >
        <Plus className="h-6 w-6" />
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Quick Actions</DialogTitle>
          </DialogHeader>

          {!actionType ? (
            <div className="grid grid-cols-2 gap-3 py-4">
              {actions.map((action) => {
                const Icon = action.icon;
                return (
                  <Button
                    key={action.type}
                    onClick={() => setActionType(action.type)}
                    variant="outline"
                    className={`h-24 flex-col gap-2 ${action.bgColor} border-0`}
                  >
                    <Icon className={`h-6 w-6 ${action.color}`} />
                    <span className="text-sm font-medium">{action.label}</span>
                  </Button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {/* Mood Logging */}
              {actionType === 'mood' && (
                <>
                  <div className="space-y-3">
                    <Label className="text-sm text-gray-600">How are you feeling?</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {MOOD_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setSelectedMood(option.value)}
                          className={`flex flex-col items-center p-2 rounded-xl border-2 transition-all ${
                            selectedMood === option.value
                              ? 'bg-pink-100 border-pink-400 scale-105'
                              : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span className="text-xl">{option.emoji}</span>
                          <span className="text-[10px] text-gray-600 mt-1">{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-600 flex items-center gap-2">
                      <Zap className="h-4 w-4 text-yellow-500" />
                      Energy: {energyLevel}/10
                    </Label>
                    <div className="flex gap-1">
                      {[1,2,3,4,5,6,7,8,9,10].map((level) => (
                        <button
                          key={level}
                          onClick={() => setEnergyLevel(level)}
                          className={`flex-1 py-2 rounded text-xs font-medium transition-all ${
                            energyLevel >= level
                              ? 'bg-yellow-400 text-yellow-900'
                              : 'bg-gray-100 text-gray-400'
                          }`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleMoodLog}
                    className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
                    disabled={!selectedMood || loading}
                  >
                    {loading ? 'Logging...' : '✨ Log Mood'}
                  </Button>
                </>
              )}

              {actionType === 'task' && (
                <>
                  <div className="space-y-2">
                    <Label>Task Title</Label>
                    <Input
                      placeholder="Enter task title..."
                      value={taskTitle}
                      onChange={(e) => setTaskTitle(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <Button
                    onClick={() => handleQuickAction('task', { title: taskTitle })}
                    className="w-full"
                    disabled={!taskTitle.trim()}
                  >
                    Create Task
                  </Button>
                </>
              )}

              {actionType === 'water' && (
                <>
                  <div className="space-y-2">
                    <Label>Glasses of Water</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={waterGlasses}
                      onChange={(e) => setWaterGlasses(parseInt(e.target.value))}
                      autoFocus
                    />
                  </div>
                  <Button
                    onClick={() => handleQuickAction('water', { glasses: waterGlasses })}
                    className="w-full"
                  >
                    Log Water
                  </Button>
                </>
              )}

              {actionType === 'workout' && (
                <Button
                  onClick={() => handleQuickAction('workout', { name: 'Quick Workout', duration: 30, calories: 150 })}
                  className="w-full"
                >
                  Log 30-Min Workout
                </Button>
              )}

              {actionType === 'event' && (
                <>
                  <div className="space-y-2">
                    <Label>Event Title</Label>
                    <Input
                      placeholder="Enter event title..."
                      value={taskTitle}
                      onChange={(e) => setTaskTitle(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <Button
                    onClick={() => handleQuickAction('event', { title: taskTitle, date: new Date().toISOString().split('T')[0] })}
                    className="w-full"
                    disabled={!taskTitle.trim()}
                  >
                    Add Event
                  </Button>
                </>
              )}

              <Button
                variant="ghost"
                onClick={() => setActionType(null)}
                className="w-full"
              >
                ← Back
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuickActionButton;
